// Очередь с лимитом одновременных задач (UPQ-2, из форка «Наш.Чат» 03.11.2026). Композер ставит в неё
// загрузки вложений: прод 04.10 — 13 файлов стартовали разом и поделили мобильный канал поровну, за
// минуту не дошёл ни один. По 2 одновременно каждый файл получает заметную долю канала и
// доходит, остальные ждут своей очереди.
//
// Модуль ничего не знает о загрузках и React: задача — функция, возвращающая промис; исход задачи
// (успех/ошибка) — забота самой задачи, очередь лишь освобождает место и запускает следующую.

export type QueueTaskStart = () => Promise<unknown>

export class ConcurrencyQueue {
  private readonly limit: number
  private waiting: Array<{ id: string; start: QueueTaskStart }> = []
  /** Бегущие: id → токен запуска. Завершение освобождает место, только если токен ещё его. */
  private readonly running = new Map<string, object>()

  constructor(limit: number) {
    if (!Number.isInteger(limit) || limit < 1) throw new RangeError('limit must be an integer >= 1')
    this.limit = limit
  }

  /** Поставить задачу в конец очереди (id уникален среди ждущих/бегущих — повтор игнорируется). */
  enqueue(id: string, start: QueueTaskStart): void {
    if (this.running.has(id) || this.waiting.some((t) => t.id === id)) return
    this.waiting.push({ id, start })
    this.pump()
  }

  /** Убрать ждущую задачу (бегущую останавливает её владелец и отпускает через release). */
  remove(id: string): void {
    this.waiting = this.waiting.filter((t) => t.id !== id)
  }

  /**
   * Отпустить место бегущей задачи сразу (владелец её оборвал): следующая стартует, не дожидаясь,
   * пока оборванная докатится до своего catch. Её позднее завершение уже ничего не освобождает.
   */
  release(id: string): void {
    if (!this.running.delete(id)) return
    this.pump()
  }

  /**
   * Забыть всё: ждущие выброшены, бегущие отвязаны (владелец их уже оборвал). Место под новые
   * задачи свободно сразу — оборванные не держат лимит, пока докатываются до своего catch.
   */
  clear(): void {
    this.waiting = []
    this.running.clear()
  }

  isQueued(id: string): boolean {
    return this.waiting.some((t) => t.id === id)
  }

  isRunning(id: string): boolean {
    return this.running.has(id)
  }

  get runningCount(): number {
    return this.running.size
  }

  get queuedCount(): number {
    return this.waiting.length
  }

  private pump(): void {
    while (this.running.size < this.limit && this.waiting.length > 0) {
      const task = this.waiting.shift() as { id: string; start: QueueTaskStart }
      const token = {}
      this.running.set(task.id, token)
      let done: Promise<unknown>
      try {
        done = task.start()
      } catch (err) {
        done = Promise.reject(err)
      }
      void done
        .catch(() => undefined)
        .finally(() => {
          // отпущена (release/clear) или id уже перезапущен — это место ей больше не принадлежит
          if (this.running.get(task.id) !== token) return
          this.running.delete(task.id)
          this.pump()
        })
    }
  }
}
